/**
 * The Quantity type. CLAUDE.md §16.1:
 *   "packages/units exports Quantity { value, unit }. Clinical numbers cross
 *    module boundaries only as Quantity."
 *
 * The unit is a type parameter, so `Quantity<'mg/dL'>` and `Quantity<'mmol/L'>`
 * are not mutually assignable and mixing them is a compile-time error — the
 * "compile-time error where possible" half of §16.1. The runtime half is in
 * convert.ts and arithmetic.ts, which return Result rather than a silent value.
 */
import { fail, ok } from './result.js';
import { CANONICAL_UNIT, UNITS, dimensionOf, displayOf, isUcumCode, } from './ucum.js';
/**
 * Plausibility bounds per dimension, expressed in that dimension's CANONICAL
 * unit. These are NOT clinical reference ranges — §16.4 is explicit that
 * reference ranges are population- and lab-specific and come from the source
 * system, never hardcoded. These are far wider: they exist only to catch a unit
 * mix-up or a transposed field before it reaches storage.
 *
 * Checking in canonical units is the point. Checking in the supplied unit would
 * make the bound mean something different for each unit — 900 g and 900 kg would
 * both be tested against the same numbers.
 */
const PLAUSIBLE_RANGE = {
    'mass-concentration': { min: 0, max: 100_000 },
    'substance-concentration': { min: 0, max: 100_000 },
    mass: { min: 0, max: 700 }, // kg; above the heaviest human ever recorded
    length: { min: 0, max: 300 },
    'mass-per-mass': { min: 0, max: 100_000 },
    'mass-per-substance': { min: 0, max: 100_000 },
    egfr: { min: 0, max: 250 },
    time: { min: 0, max: 150 },
    'catalytic-activity': { min: 0, max: 100_000 },
    'count-concentration': { min: 0, max: 10_000 },
    pressure: { min: 0, max: 400 },
    osmolality: { min: 0, max: 600 },
    'insulin-dose': { min: 0, max: 1_000 },
    fraction: { min: -1_000, max: 1_000 },
    'hba1c-ifcc': { min: 0, max: 300 },
    'body-mass-index': { min: 0, max: 300 }, // kg/m²; the highest ever recorded is ~185
    dimensionless: { min: Number.NEGATIVE_INFINITY, max: Number.POSITIVE_INFINITY },
};
/**
 * Construct a Quantity, validating the number is real and within plausible
 * bounds for its dimension. Returns Result — never throws, never defaults.
 */
export const quantity = (value, unit) => {
    if (!Number.isFinite(value)) {
        return fail('NOT_FINITE', `Quantity value must be a finite number, received ${String(value)}.`, {
            unit,
        });
    }
    const dimension = dimensionOf(unit);
    const range = PLAUSIBLE_RANGE[dimension];
    const canonicalValue = value * UNITS[unit].toCanonical;
    if (canonicalValue < range.min || canonicalValue > range.max) {
        return fail('OUT_OF_RANGE', `Value ${value} ${unit} is outside the plausible range for ${dimension} ` +
            `(${range.min}–${range.max} ${CANONICAL_UNIT[dimension]}). ` +
            'This usually means a unit mix-up rather than a real measurement.', { unit, value, min: range.min, max: range.max });
    }
    return ok({ value, unit });
};
/**
 * Escape hatch for values already validated at a trusted boundary (a database
 * read of a quantity written through `quantity()`, or a literal in a test).
 * Deliberately verbose so it is visible in review and greppable in audit.
 */
export const unsafeQuantity = (value, unit) => ({
    value,
    unit,
});
/** Parse an untrusted `{ value, unit }` pair, e.g. from a FHIR Quantity element. */
export const parseQuantity = (input) => {
    if (input.value === undefined || input.value === null) {
        return fail('MISSING_INPUT', 'Quantity is missing its value. A missing value is not zero (§16.3).');
    }
    if (input.unit === undefined || input.unit === '') {
        return fail('MISSING_INPUT', 'Quantity is missing its unit. A bare number representing a clinical value is a bug, not a shortcut (§16.1).');
    }
    if (!isUcumCode(input.unit)) {
        return fail('UNKNOWN_UNIT', `"${input.unit}" is not a UCUM code registered in @glycemia/units.`, {
            unit: input.unit,
        });
    }
    return quantity(input.value, input.unit);
};
export const isQuantity = (v) => typeof v === 'object' &&
    v !== null &&
    typeof v.value === 'number' &&
    typeof v.unit === 'string' &&
    isUcumCode(v.unit);
export const sameDimension = (a, b) => dimensionOf(a.unit) === dimensionOf(b.unit);
/** Unrounded debug/log rendering. For patient-facing output use format() in round.ts. */
export const describe = (q) => `${q.value} ${displayOf(q.unit)}`.trim();
//# sourceMappingURL=quantity.js.map